import { removeObjectUndefinedOrNullValues } from '@schooly/utils/remove-object-undefined-or-null-values';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  Application,
  ApplicationsByChildrenIds,
  ApplicationsByChildrenIdsRequest,
  ApplicationShort,
  ApplicationStatus,
} from './apiTypes/applications';
import {
  ApplicationConvertRequest,
  ApplicationCreateRequest,
  ApplicationCreateResponse,
  ApplicationPublicCreateRequest,
  ApplicationPublicCreateResponse,
  ApplicationRejectRequest,
  ApplicationUpdateNotesRequest,
  ApplicationUpdateRequest,
  GetAvailableAdultsForApplicationRequest,
  GetAvailableAdultsForApplicationResponse,
} from './apiTypes/endpoints/applications';
import { ApiError, PagedResponse } from './apiTypes/misc';
import {
  applicationCreatePath,
  applicationCreatePublicPath,
  applicationCreateStaffPath,
} from './apiTypes/paths/applications';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import { School } from './apiTypes/schools';
import { UserFilter } from './apiTypes/users';
import * as api from './requests';

const APPLICATIONS_URL = '/application';

type GetApplicationsRequest = {
  school_id: School['id'];
  status: ApplicationStatus;
  filters?: Partial<UserFilter>;
  page_number?: number;
  page_size?: number;
  query?: string;
};

export const GET_APPLICATIONS_QUERY = `${APPLICATIONS_URL}/GET_APPLICATIONS_QUERY`;

export function getApplications({
  school_id,
  status,
  filters,
  page_number,
  page_size,
  query,
}: GetApplicationsRequest): Promise<PagedResponse<ApplicationShort>> {
  const params = removeObjectUndefinedOrNullValues({
    page_number,
    page_size,
    search_query: query || undefined,
    filters,
  });

  return api.get(`${APPLICATIONS_URL}/list?school_id=${school_id}&status=${status}`, {
    params,
  });
}

export const useGetApplicationsQuery = (
  initialParams: Omit<GetApplicationsRequest, 'filters' | 'sort'>,
  options?: RQUseInfiniteQueryOptions<PagedResponse<ApplicationShort>>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<PagedResponse<ApplicationShort>, ApiError>(
    [GET_APPLICATIONS_QUERY, params],
    ({ pageParam }) =>
      getApplications({
        page_number: pageParam,
        ...params,
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export function getApplication(id: Application['id']): Promise<Application> {
  return api.get(`${APPLICATIONS_URL}/${id}`);
}

export function getApplicationsByStudentIds({
  children_ids,
  school_user_relation_id,
}: ApplicationsByChildrenIdsRequest): Promise<ApplicationsByChildrenIds[]> {
  return api.get(`${APPLICATIONS_URL}/for-children`, {
    params: {
      children_ids,
      school_user_relation_id,
    },
  });
}

export const GET_APPLICATIONS_BY_STUDENT_IDS_QUERY = `${APPLICATIONS_URL}/GET_APPLICATIONS_BY_STUDENT_IDS_QUERY`;

export const useGetApplicationsByStudentIdsQuery = (
  data: ApplicationsByChildrenIdsRequest,
  options?: RQUseQueryOptions<ApplicationsByChildrenIds[]>,
) => {
  return useQuery<ApplicationsByChildrenIds[], ApiError>(
    [GET_APPLICATIONS_BY_STUDENT_IDS_QUERY, data],
    () => getApplicationsByStudentIds(data),
    options,
  );
};

export function getApplicationStatuses(): Promise<Array<ApplicationStatus>> {
  return api.get(`${APPLICATIONS_URL}/statuses`);
}

export function createParentApplication({
  schoolId,
  ...params
}: ApplicationCreateRequest): Promise<ApplicationCreateResponse> {
  return api.post(applicationCreatePath({ school_id: schoolId }), params);
}

export const CREATE_PARENT_APPLICATION_MUTATION = `${APPLICATIONS_URL}/CREATE_PARENT_APPLICATION_MUTATION`;

export const useCreateParentApplicationMutation = (
  options?: RQUseMutationOptions<ApplicationCreateResponse, ApplicationCreateRequest>,
): RQUseMutationResult<ApplicationCreateResponse, ApplicationCreateRequest> => {
  return useMutation([CREATE_PARENT_APPLICATION_MUTATION], createParentApplication, {
    ...options,
  });
};

export function createStaffApplication({
  schoolId,
  ...params
}: ApplicationCreateRequest): Promise<ApplicationCreateResponse> {
  return api.post(applicationCreateStaffPath({ school_id: schoolId }), params);
}

export const CREATE_STAFF_APPLICATION_MUTATION = `${APPLICATIONS_URL}/CREATE_STAFF_APPLICATION_MUTATION`;

export const useCreateStaffApplicationMutation = (
  options?: RQUseMutationOptions<ApplicationCreateResponse, ApplicationCreateRequest>,
): RQUseMutationResult<ApplicationCreateResponse, ApplicationCreateRequest> => {
  return useMutation([CREATE_STAFF_APPLICATION_MUTATION], createStaffApplication, {
    ...options,
  });
};

export function createApplicationPublic({
  schoolId,
  ...params
}: ApplicationPublicCreateRequest): Promise<ApplicationPublicCreateResponse> {
  return api.post(applicationCreatePublicPath({ school_id: schoolId }), params);
}

export const CREATE_APPLICATION_PUBLIC_MUTATION = `${APPLICATIONS_URL}/CREATE_APPLICATION_PUBLIC_MUTATION`;

export const useCreateApplicationPublicMutation = (
  options?: RQUseMutationOptions<ApplicationPublicCreateResponse, ApplicationPublicCreateRequest>,
): RQUseMutationResult<ApplicationPublicCreateResponse, ApplicationPublicCreateRequest> => {
  return useMutation([CREATE_APPLICATION_PUBLIC_MUTATION], createApplicationPublic, {
    ...options,
  });
};

export function updateApplication({
  id,
  ...params
}: ApplicationUpdateRequest): Promise<Omit<ApplicationCreateResponse, 'application_id'>> {
  return api.patch(`${APPLICATIONS_URL}/${id}`, params);
}

export function updateApplicationNotes({ id, ...params }: ApplicationUpdateNotesRequest): Promise<{
  success: 'string';
}> {
  return api.patch(`${APPLICATIONS_URL}/${id}/notes`, params);
}

export function convertApplication({
  id,
  ...params
}: ApplicationConvertRequest): Promise<{ status: 'success' }> {
  return api.patch(`${APPLICATIONS_URL}/${id}/convert`, params);
}

export const useConvertApplication = (
  options?: RQUseMutationOptions<{ status: 'success' }, ApplicationConvertRequest>,
): RQUseMutationResult<{ status: 'success' }, ApplicationConvertRequest> => {
  return useMutation(convertApplication, options);
};

export function rejectApplication({
  id,
  ...data
}: ApplicationRejectRequest): Promise<{ status: 'success' }> {
  return api.patch(`${APPLICATIONS_URL}/${id}/reject`, data);
}

export function getAvailableAdultsForApplications(
  params: GetAvailableAdultsForApplicationRequest,
): Promise<GetAvailableAdultsForApplicationResponse> {
  const queryParams = removeObjectUndefinedOrNullValues({
    children_ids: params.children_ids?.join(','),
  });

  return api.get(`${APPLICATIONS_URL}/available-adults`, { params: queryParams });
}

export const GET_AVAILABLE_ADULTS_FOR_APPLICATIONS_QUERY = `${APPLICATIONS_URL}/GET_AVAILABLE_ADULTS_FOR_APPLICATIONS_QUERY`;

export const useGetAvailableAdultsForApplicationsQuery = (
  params: GetAvailableAdultsForApplicationRequest,
  options?: RQUseQueryOptions<GetAvailableAdultsForApplicationResponse>,
) => {
  return useQuery<GetAvailableAdultsForApplicationResponse, ApiError>(
    [GET_AVAILABLE_ADULTS_FOR_APPLICATIONS_QUERY, params],
    () => getAvailableAdultsForApplications(params),
    options,
  );
};
